import styles from './styles/component.product-card.module.scss';
import { getURLsFromHit } from '../Cards/Product Card/getURLsFromHit';
import { createImage } from '../Cards/Product Card/createImage';
import { createWishlistButton } from '../Cards/Product Card/createWishlistButton';
import { getIconName } from '../Cards/Product Card/getIconName';
import { createBadges } from '../Cards/Product Card/createBadges';
import { isNullOrUndef } from '../utils/isNullOrUndef';
import { seoGetKeywords } from '../algolia-react/utils/seo.getKeywords';
import { handleize } from '../utils/handleize';
import { a11yTextVNode } from '../js/a11y';
import { mouseAndWheelHorizontalScrollEvents } from '../js/helpers/mouseAndWheelHorizontalScrollEvents';
import { compareLayoutsByPrecedence } from '../utils/compareLayoutsByPrecedence';
import { dlDispatchAmplEvent } from '../events/dl.dispatchAmplEvent';
const vn = window.vNode;
const areLayoutsFull = (layouts) => {
    return !layouts.some(({ name, type, pieces, shape, room_previews }) => {
        return name === undefined ||
            type === undefined ||
            pieces === undefined ||
            shape === undefined ||
            room_previews === undefined;
    });
};
class ComponentProductCard extends HTMLElement {
    vNode = null;
    connected = false;
    hit;
    filters;
    imgSizes;
    urls = undefined;
    _clickCallback;
    get _hit() {
        return this.hit;
    }
    set _hit(hit) {
        this.hit = {
            ...hit,
            layouts: [...hit.layouts ?? []].sort(({ name: a }, { name: b }) => {
                return compareLayoutsByPrecedence(a, b);
            })
        };
        if (this.connected && this.filters !== undefined) {
            this.getAndSetURLs();
            this.updateComponent();
        }
        if (this.hit.layouts.length < 1) {
            dlDispatchAmplEvent('error', {
                details: 'Algolia DB - missed layouts',
                target: this.hit.objectID
            });
        }
    }
    get _filters() {
        return this.filters;
    }
    set _filters(filters) {
        this.filters = filters;
        if (this.connected && this.hit !== undefined) {
            this.getAndSetURLs();
            this.updateComponent();
        }
    }
    get _imageSizes() {
        return this.imgSizes;
    }
    set _imageSizes(sizes) {
        this.imgSizes = sizes;
        this.connected && this.updateComponent();
    }
    getAndSetURLs() {
        if (this.hit.layouts.length > 0) {
            this.urls = getURLsFromHit({ ...this.filters, item: this.hit });
        }
    }
    createVNode() {
        const [kw1, kw2] = seoGetKeywords({ layoutKey: this.urls?.selectedLayout.type });
        const title = this.hit.title
            .replace(/multi panel|canvas wall art|wall art|personalized/i, '')
            .trim();
        const activeLayoutIndex = this.urls !== undefined ? this.hit.layouts.indexOf(this.urls.selectedLayout) : 0;
        return vn.create('div', {
            class: `pos:r ${styles.wrapper}`
        }, [
            vn.create('div', {
                class: 'pos:r'
            }, [
                createImage({
                    hit: this.hit,
                    title,
                    urls: this.urls,
                    sizes: this.imgSizes,
                    keywords: { kw1, kw2 },
                    clickCallback: () => {
                        this._clickCallback?.(this.hit);
                    },
                    image3D: this._filters.rooms.length < 1
                }),
                ...(this.hit.layouts.length > 0 &&
                    areLayoutsFull(this.hit.layouts) &&
                    window.matchMedia('(min-width: 1200px)').matches
                    ? [
                        ...(this.hit.layouts.length > 1
                            ? [
                                vn.create('button', {
                                    class: `btn:cleared pos:a t:50p trf:ty:-50p l:0 d:flex ai:center jc:center trs ${styles.arrowButton}`,
                                    events: {
                                        click: () => {
                                            this.urls = getURLsFromHit({
                                                ...this.filters,
                                                item: this.hit,
                                                selectedLayout: activeLayoutIndex - 1 < 0
                                                    ? this.hit.layouts[this.hit.layouts.length - 1]
                                                    : this.hit.layouts[activeLayoutIndex - 1]
                                            });
                                            this.updateComponent();
                                        }
                                    }
                                }, [
                                    vn.create('s-svg', { 'data-icon': '#icon--arrow-slider-left' })
                                ]),
                                vn.create('button', {
                                    class: `btn:cleared pos:a t:50p trf:ty:-50p r:0 d:flex ai:center jc:center trs ${styles.arrowButton}`,
                                    events: {
                                        click: () => {
                                            this.urls = getURLsFromHit({
                                                ...this.filters,
                                                item: this.hit,
                                                selectedLayout: activeLayoutIndex + 1 > this.hit.layouts.length - 1
                                                    ? this.hit.layouts[0]
                                                    : this.hit.layouts[activeLayoutIndex + 1]
                                            });
                                            this.updateComponent();
                                        }
                                    }
                                }, [
                                    vn.create('s-svg', { 'data-icon': '#icon--arrow-slider-right' })
                                ])
                            ]
                            : []),
                        vn.create('div', {
                            class: `d:flex jc:between pos:a l:0 w:100p ${styles.buttonsRow}`
                        }, [
                            vn.create('div', {
                                class: 'fx:1 d:flex ov:h'
                            }, [
                                vn.create('ul', {
                                    class: `list:none trs maw:100p scrbar:none ov:x:a ov:y:h ${styles.layouts}`,
                                    events: {
                                        mousedown: mouseAndWheelHorizontalScrollEvents.mousedown
                                    }
                                }, this.hit.layouts.map((layout, i) => {
                                    const iconName = getIconName(layout.name);
                                    return vn.create('li', {
                                        key: layout.name
                                    }, [
                                        vn.create('button', {
                                            class: `btn:cleared d:flex ai:center jc:center bdrs:1 trs hover:bxsh:3 ${styles.layoutButton}`,
                                            'data-cy-test': 'ProductCardLayoutBtn',
                                            ...(i === activeLayoutIndex
                                                ? { 'data-active': '' }
                                                : {
                                                    events: {
                                                        click: () => {
                                                            this.urls = getURLsFromHit({ ...this.filters, item: this.hit, selectedLayout: layout });
                                                            this.updateComponent();
                                                            dlDispatchAmplEvent('PCard - Layout icon clicked', {
                                                                layoutName: layout.name
                                                            });
                                                        }
                                                    }
                                                })
                                        }, [
                                            a11yTextVNode('Switch layout to ' + layout.name),
                                            vn.create('s-svg', { class: styles.layoutButtonIcon, 'data-icon': `#${iconName}` })
                                        ])
                                    ]);
                                }))
                            ]),
                            vn.create('button', {
                                class: `btn:cleared lh:0 bdrs:1 hover:bxsh:3 trs ${styles.layoutButton} ${styles.quickViewButton}`,
                                events: {
                                    click: () => {
                                        void import('../Cards/Product Card/quickView').then(({ quickView }) => {
                                            this.urls !== undefined && quickView(this.hit, this.urls, (selectedLayout) => {
                                                this.urls = getURLsFromHit({ ...this.filters, item: this.hit, selectedLayout });
                                                this.updateComponent();
                                            });
                                        });
                                    }
                                }
                            }, [
                                vn.create('s-svg', {
                                    class: styles.quickViewButtonIcon,
                                    'data-icon': '#icon--magnifying-plus'
                                })
                            ])
                        ])
                    ]
                    : [])
            ]),
            vn.create('div', {
                class: styles.titleWrapper
            }, [
                vn.create('span', {
                    class: `fw:700 lts:001 ${styles.title}`
                }, [title]),
                createWishlistButton(this.hit),
                ...(isNullOrUndef(this.hit.artist_name) || this.hit.artist_name.length < 1
                    ? []
                    : [vn.create('a', {
                            href: `/collections/${handleize(this.hit.artist_name)}-wall-art`,
                            class: `ff:abril-display text:italic text:capitalize ${styles.artist}`
                        }, [this.hit.artist_name])])
            ]),
            ...createBadges(this.hit)
        ]);
    }
    updateComponent() {
        try {
            const nVNode = this.createVNode();
            vn.patch(this.vNode, nVNode, this);
            this.vNode = nVNode;
        }
        catch (e) {
            dlDispatchAmplEvent('error', {
                details: 'Algolia DB - broken item',
                target: this.hit.objectID
            });
        }
    }
    connectedCallback() {
        if (this._hit !== undefined && this._filters !== undefined) {
            this.hit = {
                ...this._hit,
                layouts: [...this._hit.layouts ?? []].sort(({ name: a }, { name: b }) => {
                    return compareLayoutsByPrecedence(a, b);
                })
            };
            this.filters = this._filters;
            this.getAndSetURLs();
            this.updateComponent();
            if (this.hit.layouts.length < 1) {
                dlDispatchAmplEvent('error', {
                    details: 'Algolia DB - missed layouts',
                    target: this.hit.objectID
                });
            }
        }
        this.connected = true;
    }
}
window.customElements.define('product-card-new-component', ComponentProductCard);
